import { useCallback, useEffect, useState } from 'react';
import { Input } from '../components/ui/Input/Input';
import {
  PrimaryButton,
  TextButton,
  TextSecondaryButton,
  WhiteButton
} from '../components/ui/Button/Buttons';
import {
  LeftArrow,
  PlusBlue,
  ResendIcon,
  Search,
  SortIcon,
  SparklesIcon,
  TrashCan
} from '../icons/SvgIcons';
import DropdownPortal from '../components/ui/DropdownPortal/DropdownPortal';
import { SearchSection } from '../components/features/SearchSection/SearchSection';
import { useAppSelector } from '../hooks/redux';
import { useDispatch } from 'react-redux';
import {
  setAssignAiSystemWithoutInvitationSuccess,
  setCurrentUser,
  setIsRolePerAiSystemChangedSuccess,
  setRemoveUserFromSingleAiSystemSuccess
} from '../store/slices/userManagementSlice';
import {
  AiSystemsForUserDTO,
  AssignablePlatformRole,
  Role,
  UpdateUserRoleRequest
} from '../types/Team.types';
import Loader from '../components/ui/Loader/Loader';
import { useUserManagement } from '../components/hooks/useUserManagement';
import ModalWarning from '../components/ui/Modal/ModalWarning';
import { setShowWarningModal } from '../store/slices/accountSlice';
import { Toast } from '../components/ui/Toast/Toast';
import { EmptyState } from '../components/ui/EmptyState/EmptyState';
import Modal from '../components/ui/Modal/Modal';
import { getAiSystemsForUser } from '../services/TeamService';
import { TruncatedText } from '../utils/TruncatedText';
import { AssignUser } from '../components/UserManagement/AssignUser';
import {
  selectIsPlatformAdmin,
  selectIsSuperAdmin,
  selectIsUserRole
} from '../store/selectors/authSelectors';
// Aliased: this component already has a local `showToast` boolean for the
// "Changes successfully saved!" toast, which would shadow the action creator.
import { showToast as showGlobalToast } from '../store/slices/toastSlice';
import { getApiErrorMessage, isApiErrorCode } from '../utils/apiError';

export function RegisteredUserProfile() {
  const dispatch = useDispatch();
  const {
    currentUser,
    aiSystemsForUser,
    aiSystemsForUserLoading,
    isRolePerAiSystemChangedSuccess,
    removeUserFromSingleAiSystemError,
    removeUserFromSingleAiSystemLoading,
    removeUserFromSingleAiSystemSuccess,
    assignAiSystemWithoutInvitationSuccess,
    deleteUserFromAccountLoading,
    teamRoles
  } = useAppSelector((state) => state.userManagement);

  const account = useAppSelector((state) => state.accountDefault.account);

  const { total } = useAppSelector((state) => state.aiSystems);

  const {
    sendRoleChangesPerAiSystem,
    removeUserFromSingleAiSystem,
    fetchAiSystemsForUser,
    fetchUsers,
    deleteTeamMember,
    changePlatformRole,
    resendInvitationForMember
  } = useUserManagement();

  const [firstName, setFirstName] = useState(currentUser?.firstName || '-');
  const [lastName, setLastName] = useState(currentUser?.lastName || '-');
  const [email, setEmail] = useState(currentUser?.email || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [aiSystemsSearchResults, setAiSystemsSearchResults] =
    useState<AiSystemsForUserDTO | null>(null);

  const [aiSystems, setAiSystems] = useState<AiSystemsForUserDTO | null>(
    aiSystemsForUser
  );

  const [isRoleChanged, setIsRoleChanged] = useState(false);
  const [bodyRequestChangeRoles, setBodyRequestChangeRoles] =
    useState<UpdateUserRoleRequest>({ updates: [] });

  const [showToast, setShowToast] = useState(false);
  const [systemToRemove, setSystemToRemove] = useState<string>('');
  const [lastSystemToRemove, setLastSystemToRemove] = useState(false);
  const [openModalForAssignSystem, setOpenModalForAssignSystem] =
    useState(false);

  const showWarningModal = useAppSelector(
    (state) => state.account.showWarningModal
  );

  const [deletingMember, setDeletingMember] = useState(false);
  const [assignedSystemToast, setAssignedSystemToast] = useState(false);
  const [removedSystemToast, setRemovedSystemToast] = useState(false);
  const [roleChangeTarget, setRoleChangeTarget] =
    useState<AssignablePlatformRole | null>(null);
  const [changingRole, setChangingRole] = useState(false);
  const [ownershipTransferBlocked, setOwnershipTransferBlocked] =
    useState(false);

  const isUserRole = useAppSelector(selectIsUserRole);
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);

  // The AI-system role controls (PATCH /users/{id}/roles, and remove-from-system
  // which also needs an accountId a superadmin has none of) answer 403
  // USER_NOT_AUTHORIZED for a superadmin (read-only across organizations) and for
  // a USER, so they stay gated on canManageMember. Delete and platform-role
  // changes are the exceptions a superadmin may perform across orgs — see
  // showDeleteUser and canChangePlatformRole below.
  const canManageMember = useAppSelector(selectIsPlatformAdmin);

  // Delete (registered) / revoke-invitation (pending) is permitted to an ADMIN in
  // their own org or a SUPER_ADMIN across orgs: deleteTeamMember sends no
  // accountId, so the server resolves the target's org from the record. Wider
  // than canManageMember, which still gates the AI-system role controls.
  const showDeleteUser = canManageMember || isSuperAdmin;

  // A platform role only moves for a registered member: an invited row's userId
  // is an invitation id, which PATCH /users/{id}/platform-role resolves against
  // the User table and 404s on. A SUPER_ADMIN target is refused outright with
  // PLATFORM_ROLE_CHANGE_NOT_PERMITTED. SUPER_ADMIN-only: the endpoint now
  // answers USER_NOT_AUTHORIZED for an org ADMIN, so only a superadmin may
  // promote or demote. The server is the source of truth; this only decides
  // whether to render the control.
  const canChangePlatformRole =
    isSuperAdmin &&
    Boolean(currentUser?.isRegistered) &&
    currentUser?.role !== 'SUPER_ADMIN';

  const isTargetAdmin = currentUser?.role === 'ADMIN';

  // One in-flight member action at a time: deleting while promoting is a race
  // the server answers with PLATFORM_ROLE_CHANGED_CONCURRENTLY, and the user has
  // no way to tell which of their two clicks lost.
  const memberActionsBusy = changingRole || deleteUserFromAccountLoading;

  // A superadmin may resend platform invitations even though they are otherwise
  // read-only across organizations; a USER may not resend at all. An admin may
  // not resend an ADMIN-targeted invitation, and the server refuses that with a
  // bare 403 carrying no error code — so there is nothing to branch on after the
  // fact and the only clean answer is not to offer it. `role` on an invited row
  // is the invited role, which is exactly what is being refused.
  //
  // isRegistered is held here rather than at the call site so this predicate is
  // complete on its own, matching canChangePlatformRole above: only a pending
  // invitation can be resent, since a registered member already accepted theirs.
  const canResendInvitation =
    !currentUser?.isRegistered &&
    !isUserRole &&
    (isSuperAdmin || currentUser?.role !== 'ADMIN');

  const isAllSystemsAssigned = aiSystems
    ? aiSystems.items.length >= total
    : false;

  const handleRoleChanges = () => {
    if (bodyRequestChangeRoles.updates.length === 0) return;

    if (!currentUser) return;
    sendRoleChangesPerAiSystem(currentUser.userId, bodyRequestChangeRoles);
  };

  useEffect(() => {
    if (removeUserFromSingleAiSystemError) {
      console.error(
        'Error removing user from AI system:',
        removeUserFromSingleAiSystemError
      );
      return;
    }

    if (currentUser && currentUser.role !== 'SUPER_ADMIN') {
      fetchAiSystemsForUser(currentUser.userId);
    }
  }, [removeUserFromSingleAiSystemError]);

  useEffect(() => {
    if (removeUserFromSingleAiSystemSuccess && currentUser) {
      fetchAiSystemsForUser(currentUser.userId);
      fetchUsers();
      setRemovedSystemToast(true);
      dispatch(setRemoveUserFromSingleAiSystemSuccess(false));
    }
  }, [removeUserFromSingleAiSystemSuccess]);

  useEffect(() => {
    if (isRolePerAiSystemChangedSuccess) {
      setShowToast(true);
      setIsRoleChanged(false);
      setBodyRequestChangeRoles({ updates: [] });
      dispatch(setIsRolePerAiSystemChangedSuccess(false));
    }
  }, [isRolePerAiSystemChangedSuccess, account, currentUser]);

  const handleCancel = () => {
    if (!aiSystemsForUser) return;
    setAiSystems(aiSystemsForUser);
    setIsRoleChanged(false);
    setBodyRequestChangeRoles({ updates: [] });
  };

  const roles = [
    { value: 'EDITOR', label: 'Editor' },
    { value: 'VIEWER', label: 'Viewer' }
  ];

  useEffect(() => {
    setAiSystems(aiSystemsForUser);
    setShowToast(false);
  }, [aiSystemsForUser]);

  useEffect(() => {
    if (!aiSystems || !aiSystemsForUser) {
      setIsRoleChanged(false);
      setBodyRequestChangeRoles({ updates: [] });
      return;
    }

    const updates: { aiSystemId: string; role: Role }[] = [];

    for (const currentSystem of aiSystems.items) {
      const originalSystem = aiSystemsForUser.items.find(
        (original) => original.aiSystemId === currentSystem.aiSystemId
      );

      if (originalSystem && originalSystem.role !== currentSystem.role) {
        updates.push({
          aiSystemId: currentSystem.aiSystemId,
          role: currentSystem.role
        });
      }
    }

    const hasChanges = updates.length > 0;

    setIsRoleChanged(hasChanges);
    setBodyRequestChangeRoles({ updates });
  }, [aiSystems]);

  const handleBack = () => {
    dispatch(setCurrentUser(null));
    setLastSystemToRemove(false);
  };

  // ModalWarning owns neither its open state nor its Cancel button: both hang
  // off the global showWarningModal, and Cancel clears only that. Three
  // different confirmations share the flag here, discriminated by local state,
  // so without this a cancelled delete leaves deletingMember true and the next
  // confirmation renders two dialogs stacked on top of each other.
  useEffect(() => {
    if (showWarningModal) return;

    setDeletingMember(false);
    setSystemToRemove('');
    setRoleChangeTarget(null);
  }, [showWarningModal]);

  const handleDelete = () => {
    dispatch(setShowWarningModal(true));
    setDeletingMember(true);
  };

  async function confirmDelete() {
    if (!currentUser) return;

    dispatch(setShowWarningModal(false));

    const deleted = await deleteTeamMember(
      currentUser.userId,
      currentUser.isRegistered,
      currentUser.invitationStatus === 'PENDING' &&
        currentUser.role === 'SUPER_ADMIN'
    );

    // Only leave on success. The previous version fired the request and called
    // handleBack() in the same tick, so this component was unmounted before the
    // response arrived: a refusal had nothing left to render into and the row
    // quietly stayed in the list. Staying put also keeps the retry one click
    // away instead of behind a re-navigation.
    if (!deleted) return;

    // Dispatched before handleBack because this component is about to unmount —
    // which is exactly why the old success toast had to live on the parent page,
    // where currentUser is already null and the row type is unknowable.
    dispatch(
      showGlobalToast({
        message: currentUser.isRegistered
          ? 'User successfully deleted!'
          : 'Invitation successfully revoked!',
        type: 'success'
      })
    );
    handleBack();
  }

  const handlePlatformRoleChange = () => {
    setOwnershipTransferBlocked(false);
    setRoleChangeTarget(isTargetAdmin ? 'USER' : 'ADMIN');
    dispatch(setShowWarningModal(true));
  };

  async function confirmRoleChange() {
    if (!currentUser || !roleChangeTarget) return;

    // Captured before the modal closes: the cleanup effect above nulls
    // roleChangeTarget on the very next render.
    const nextRole = roleChangeTarget;

    dispatch(setShowWarningModal(false));
    setChangingRole(true);

    try {
      const result = await changePlatformRole(currentUser.userId, nextRole);

      // Reaching changed:false means currentUser.role was stale — the button
      // offered an action the server considered already done — so say that
      // rather than staying silent after a confirmed modal. Client-composed and
      // past tense: the server's copy ("User already has the ADMIN role.") is a
      // state description, and dressing it as a success would make a
      // double-click read as two promotions.
      dispatch(
        result.changed
          ? showGlobalToast({ message: result.message, type: 'success' })
          : showGlobalToast({
              // A no-op is reachable both ways: promoting an existing ADMIN, or
              // demoting someone who is already a USER.
              message:
                nextRole === 'ADMIN'
                  ? `${currentUser.email} was already an organization admin.`
                  : `${currentUser.email} was already not an organization admin.`,
              type: 'info'
            })
      );

      // currentUser is a snapshot taken when the row was clicked, so without
      // this the header keeps offering the action that just succeeded.
      dispatch(setCurrentUser({ ...currentUser, role: result.role }));
    } catch (error) {
      if (isApiErrorCode(error, 'ADMIN_NOT_FOUND_FOR_OWNERSHIP_TRANSFER')) {
        setOwnershipTransferBlocked(true);
      } else {
        // Every other code carries actionable server copy — the last-admin
        // codes name the fix, the lock and concurrency codes say to retry — so
        // relay it rather than inventing a second vocabulary. The request opts
        // out of the interceptor's toast, so this is the only one that fires.
        dispatch(
          showGlobalToast({
            message: getApiErrorMessage(
              error,
              "Could not change this user's role."
            ),
            type: 'error'
          })
        );
      }
    } finally {
      setChangingRole(false);
    }
  }

  const handleAssignSystem = () => {
    setOpenModalForAssignSystem(true);
  };

  const handleRemoveSystem = () => {
    dispatch(setShowWarningModal(false));
    removeUserFromSingleAiSystem(
      account!.id,
      currentUser!.userId,
      systemToRemove,
      lastSystemToRemove
    );
    setSystemToRemove('');
    if (lastSystemToRemove) {
      handleBack();
    }
  };

  const openRemoveSystemModal = (systemId: string) => {
    setSystemToRemove(systemId);
    setLastSystemToRemove(aiSystems?.items.length === 1);
    dispatch(setShowWarningModal(true));
  };

  const handleRoleChange = (systemId: string, newRole: string) => {
    if (!aiSystems) return;

    const updatedItems = aiSystems.items.map((system) =>
      system.aiSystemId === systemId
        ? { ...system, role: newRole as Role }
        : system
    );

    setAiSystems({
      ...aiSystems,
      items: updatedItems
    });
  };

  // The row's userId is the invitation id on a pending invitation, which is
  // exactly what this endpoint wants. No account is involved, so this now works
  // for a superadmin — it previously bailed out on the missing account and left
  // the button doing nothing.
  const handleResendInvitation = async () => {
    if (!currentUser) return;
    const message = await resendInvitationForMember(currentUser.userId);

    if (message) {
      dispatch(showGlobalToast({ message, type: 'success' }));
    }
  };

  const displayedAiSystems =
    searchQuery.length > 0 ? aiSystemsSearchResults : aiSystems;

  const handleSearchChange = useCallback(
    async (query: string) => {
      if (query.length === 0) {
        setAiSystemsSearchResults(null);
        return;
      }

      if (!currentUser) return;

      try {
        const response = await getAiSystemsForUser(currentUser.userId, query);
        setAiSystemsSearchResults(response.data);
      } catch (error) {
        console.error('Error searching users:', error);
        setAiSystemsSearchResults(null);
      }
    },
    [currentUser]
  );

  useEffect(() => {
    if (!currentUser) {
      setSearchQuery('');
      setAiSystemsSearchResults(null);
    }
  }, [currentUser]);

  useEffect(() => {
    if (assignAiSystemWithoutInvitationSuccess && currentUser) {
      fetchAiSystemsForUser(currentUser.userId);
      fetchUsers();
      setAssignedSystemToast(true);
      dispatch(setAssignAiSystemWithoutInvitationSuccess(false));
    }
  }, [assignAiSystemWithoutInvitationSuccess]);

  return (
    <div className="flex flex-col w-full bg-white p-4 rounded-lg">
      <div className="w-full mx-auto text-left">
        {/* Header */}
        <div className="flex items-center self-stretch flex-row mb-5 justify-between">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleBack}
              id="registered-user-profile-back-button"
              data-testid="registered-user-profile-back-button"
              className="flex gap-2 items-center text-red"
            >
              {LeftArrow()}
            </button>
            <h1 className="text-base font-medium leading-6 text-gray-950">
              {currentUser?.isRegistered
                ? currentUser.firstName + ' ' + currentUser.lastName
                : currentUser?.email}
            </h1>
          </div>
          <div className="flex flex-row gap-2">
            {canResendInvitation && (
              <TextButton
                onClick={handleResendInvitation}
                icon={<ResendIcon />}
                disabled={memberActionsBusy}
              >
                Resend Invitation
              </TextButton>
            )}
            {canChangePlatformRole && (
              <TextButton
                onClick={handlePlatformRoleChange}
                disabled={memberActionsBusy}
                id="registered-user-profile-platform-role-button"
                testId="registered-user-profile-platform-role-button"
              >
                {/* A literal verb phrase rather than getPlatformRoleLabel: a bare
                    "Admin" collides with the Editor/Viewer role dropdowns
                    rendered below, and the same map yields "Super Admin", which
                    would read as the next rung of a ladder an org admin cannot
                    climb. */}
                {isTargetAdmin ? 'Remove Admin Access' : 'Make Admin'}
              </TextButton>
            )}
            {showDeleteUser && (
              <button
                onClick={handleDelete}
                disabled={memberActionsBusy}
                // The same button means two different things per row type, and
                // the icon says neither.
                aria-label={
                  currentUser?.isRegistered
                    ? 'Delete user'
                    : 'Revoke invitation'
                }
                id="registered-user-profile-delete-button"
                data-testid="registered-user-profile-delete-button"
              >
                {TrashCan('small')}
              </button>
            )}
          </div>
        </div>

        {ownershipTransferBlocked && (
          <div className="flex items-start justify-between gap-3 bg-rose-100 p-3 rounded-md mb-5">
            <p className="text-rose-700 text-xs leading-4 text-left">
              <span className="font-medium mr-1">
                Admin access not removed:
              </span>
              this user holds the organization&apos;s account, so it has to pass
              to another admin first. The other admin in that organization
              hasn&apos;t signed in since being promoted, so we can&apos;t hand
              it to them yet. Ask them to sign in once, then try again.
            </p>
            <button
              type="button"
              onClick={() => setOwnershipTransferBlocked(false)}
              className="shrink-0 text-rose-700 text-xs font-medium"
              id="registered-user-profile-ownership-notice-dismiss"
              data-testid="registered-user-profile-ownership-notice-dismiss"
            >
              Dismiss
            </button>
          </div>
        )}

        <div
          className={`flex flex-col gap-8 px-6 mb-8 overflow-y-auto ${
            isRoleChanged ? 'h-[calc(100vh-330px)]' : 'h-[calc(100vh-270px)]'
          }`}
        >
          {/* Personal Information */}
          <div className="bg-white">
            <h2 className="text-base font-medium leading-6 mb-6 text-gray-900v2">
              Personal information
            </h2>
            <div className="grid grid-cols-2 gap-6 mb-6">
              <Input
                label="Name"
                id="firstName"
                type="text"
                required={true}
                value={firstName}
                maxLength={50}
                onChange={(e) => setFirstName(e.target.value)}
                labelClassName="label"
                inputClassName="input"
                disabled
              />
              <Input
                label="Last name"
                id="lastName"
                type="text"
                required={true}
                value={lastName}
                maxLength={50}
                onChange={(e) => setLastName(e.target.value)}
                labelClassName="label"
                inputClassName="input"
                disabled
              />
            </div>
            <div className="max-w-[calc(50%-12px)]">
              <Input
                label="Email address"
                id="email"
                type="email"
                required={true}
                value={email}
                maxLength={254}
                onChange={(e) => setEmail(e.target.value)}
                labelClassName="label"
                inputClassName="input"
                disabled
              />
            </div>
          </div>

          {/* AI Systems Assigned — only for registered members. A pending
              invite has no account yet, so member-level assign/role/remove
              actions don't apply. */}
          {currentUser?.isRegistered && (
            <div className="bg-white">
              {aiSystemsForUserLoading ||
              removeUserFromSingleAiSystemLoading ||
              aiSystems === null ? (
                <div className="absolute top-1/2 left-1/2">
                  <Loader />
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-base font-medium leading-6 text-gray-900v2">
                      AI systems Assigned
                    </h2>
                    {!isSuperAdmin && (
                      <TextButton
                        onClick={handleAssignSystem}
                        className="text-purple-800"
                        size="xs"
                        iconPosition="left"
                        icon={PlusBlue(isAllSystemsAssigned)}
                        disabled={isAllSystemsAssigned}
                      >
                        Assign AI System
                      </TextButton>
                    )}
                  </div>

                  {/* Search */}
                  <div className="mb-4">
                    <SearchSection
                      searchValue={searchQuery}
                      onSearchChange={(value) => {
                        setSearchQuery(value);
                        handleSearchChange(value);
                      }}
                      placeholder="Search by name"
                    />
                  </div>

                  {/* Table */}

                  {displayedAiSystems?.items.length === 0 ? (
                    <EmptyState
                      icon={searchQuery ? <Search /> : <SparklesIcon />}
                      title={
                        searchQuery
                          ? 'No AI Systems matched your criteria.'
                          : 'No AI Systems assigned.'
                      }
                      description={
                        searchQuery
                          ? 'Try adjusting your search.'
                          : 'Assign an AI system to enable access and permissions.'
                      }
                      classes="max-h-[200px] flex-1"
                    />
                  ) : (
                    <div className="overflow-hidden border border-gray-250v2 rounded-lg">
                      <table className="w-full table-fixed">
                        <thead className="bg-gray-40 border-b border-gray-250v2">
                          <tr>
                            <th className="w-[calc((100%-116px)/2)] px-6 py-3 text-left text-xs font-medium text-gray-550 uppercase tracking-wider">
                              <div className="flex items-center gap-2">
                                SYSTEM NAME
                                <button
                                  id="registered-user-profile-sort-system-name-button"
                                  data-testid="registered-user-profile-sort-system-name-button"
                                >
                                  <SortIcon />
                                </button>
                              </div>
                            </th>
                            <th className="w-[calc((100%-116px)/2)] px-6 py-3 text-left text-xs font-medium text-gray-550 uppercase tracking-wider">
                              ROLE
                            </th>
                            <th className="w-[116px] px-6 py-3 text-left text-xs font-medium text-gray-550 uppercase tracking-wider">
                              ACTIONS
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-250v2">
                          {displayedAiSystems?.items.map((system) => (
                            <tr key={system.aiSystemName}>
                              <td className="w-[calc((100%-116px)/2)] px-6 py-4 text-sm font-medium text-gray-900v2 align-middle">
                                <TruncatedText text={system.aiSystemName} />
                              </td>
                              <td className="w-[calc((100%-116px)/2)] px-6 py-4 text-sm text-gray-550 align-middle">
                                <div className="w-36">
                                  <DropdownPortal
                                    options={roles.filter(
                                      (role) => role.value !== system.role
                                    )}
                                    allOptions={roles}
                                    value={system.role}
                                    onChange={(e) =>
                                      handleRoleChange(system.aiSystemId, e)
                                    }
                                    size="sm"
                                    buttonClassName="h-[32px] px-[10px] py-[6px]"
                                    disabled={!canManageMember}
                                  />
                                </div>
                              </td>
                              <td className="w-[116px] px-3 py-4 text-sm align-middle">
                                  <TextSecondaryButton
                                    size="xs"
                                    iconPosition="left"
                                    onClick={() =>
                                      openRemoveSystemModal(system.aiSystemId)
                                    }
                                    className="text-red-600 hover:text-red-800 font-medium"
                                  >
                                    Remove
                                  </TextSecondaryButton>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {isRoleChanged && (
          <div className="flex justify-between items-center self-stretch pt-5 border-t border-gray-250v2">
            <WhiteButton onClick={handleCancel}>Cancel</WhiteButton>
            <PrimaryButton onClick={handleRoleChanges}>
              Save Changes
            </PrimaryButton>
          </div>
        )}
      </div>

      {showWarningModal && systemToRemove && (
        <ModalWarning
          title="Remove AI System?"
          boldDescription="Are you sure you want to remove AI system?"
          description={
            lastSystemToRemove
              ? 'User will no longer have access to data or any associated resources, including AI systems, risk assessment records, reports, and shared content.'
              : 'User will lose permission to access this AI system and all related resources, including data, risk assessment records, reports, and shared content.'
          }
          buttonText="Remove"
          handleButton={handleRemoveSystem}
        />
      )}
      {showWarningModal && roleChangeTarget && (
        <ModalWarning
          title={
            roleChangeTarget === 'ADMIN'
              ? 'Make Admin?'
              : 'Remove Admin Access?'
          }
          boldDescription={
            roleChangeTarget === 'ADMIN'
              ? `Are you sure you want to give ${currentUser?.email} admin access to the organization?`
              : `Are you sure you want to remove admin access from ${currentUser?.email}?`
          }
          description={
            roleChangeTarget === 'ADMIN'
              ? "Admins can invite and remove users, change other users' roles, and manage every AI system in the organization. The change takes effect the next time they sign in."
              : "They keep their account and their AI system assignments, but can no longer invite or remove users or change roles. If they currently hold the organization's account, it moves to another admin. The change takes effect the next time they sign in."
          }
          handleButton={confirmRoleChange}
          buttonText={
            roleChangeTarget === 'ADMIN' ? 'Make Admin' : 'Remove Admin Access'
          }
        />
      )}
      {showWarningModal && deletingMember && (
        <ModalWarning
          title={
            currentUser?.isRegistered ? 'Delete User?' : 'Revoke Invitation?'
          }
          boldDescription={
            currentUser?.isRegistered
              ? isSuperAdmin
                ? 'Are you sure you want to delete this user from the EU AI Audit account?'
                : 'Are you sure you want to delete this user from your EU AI Audit account?'
              : `Are you sure you want to revoke the invitation sent to ${currentUser?.email}?`
          }
          description={
            currentUser?.isRegistered
              ? 'The account is permanently deleted. The user will no longer have access to data or any associated resources, including AI systems, risk assessment records, reports, and shared content, and any invitations they sent are revoked.'
              : 'The invitation link stops working. Nobody loses an account — this person never created one. You can invite them again at any time.'
          }
          handleButton={confirmDelete}
          buttonText={
            currentUser?.isRegistered ? 'Delete User' : 'Revoke Invitation'
          }
        />
      )}
      {openModalForAssignSystem && (
        <Modal
          open={openModalForAssignSystem}
          setOpen={setOpenModalForAssignSystem}
          dialogPanelClassnames="min-w-[820px] overflow-hidden"
        >
          <AssignUser
            setIsOpen={setOpenModalForAssignSystem}
            registeredUserEmail={currentUser?.email}
            assignListForAiSystemFromUser={aiSystems?.items.map(
              (item) => ({ id: item.aiSystemId }) as any
            )}
            userRoles={teamRoles?.aiSystemRoles || []}
          />
        </Modal>
      )}
      {showToast && <Toast showToast={showToast} setShowToast={setShowToast} />}
      {assignedSystemToast && (
        <Toast
          text="AI System(s) successfully assigned!"
          showToast={assignedSystemToast}
          setShowToast={setAssignedSystemToast}
        />
      )}
      {removedSystemToast && (
        <Toast
          text="AI System successfully removed!"
          showToast={removedSystemToast}
          setShowToast={setRemovedSystemToast}
        />
      )}
    </div>
  );
}
